<template>
  <div>
    <h2 class="font-bold text-sm uppercase tracking-wide text-center text-black text-xl">
      🔥 Challenge exceptionnel aujourd'hui 💸
    </h2>
    <p class="mt-2 text-lg text-gray-600 text-center">Le bonus dépend des ventes totales du magasin aujourd'hui.</p>
    <p class="text-lg text-gray-600 text-center">
      Si tu as fait au moins
      <strong>1 vente</strong>
      aujourd'hui, tu touches le bonus du palier atteint par le magasin.
    </p>

    <div class="mt-2 grid grid-cols-2 gap-3">
      <div
        v-for="(tier, index) in saturdayChallengeTiers"
        :key="tier.tier"
        class="rounded-lg border p-3 text-center shadow-xs transition-shadow hover:shadow-md"
        :class="isTierReached(tier) ? 'bg-green-50 border-green-200' : ''"
      >
        <p class="text-xs font-semibold uppercase tracking-wide">Palier {{ index + 1 }}</p>
        <p class="mt-1 text-lg font-bold tabular-nums text-gray-900">
          {{ $t("roadmap.sales", tier.salesThreshold) }}
        </p>
        <p class="mt-0.5 text-sm font-semibold text-green-700">+{{ tier.bonusPerSeller }} € / vendeur</p>
      </div>
    </div>

    <div class="mt-4 rounded-xl bg-gray-100 border border-gray-200 p-4">
      <div class="flex items-center justify-center">
        <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold bg-gray-900 text-white">
          EXEMPLE AVEC DONNÉES FICTIVES
        </span>
      </div>
      <p class="text-sm text-gray-700">Si le magasin fait 6 ventes aujourd'hui.</p>
      <div class="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          v-for="seller in exampleSellers"
          :key="seller.name"
          class="rounded-lg border opacity-90 p-2 text-center shadow-xs transition-shadow hover:shadow-md"
          :class="seller.hasBonus ? 'border-green-200' : 'border-gray-200'"
        >
          <p class="text-md font-semibold text-gray-900 truncate" :title="seller.name">
            {{ seller.name }}
          </p>
          <p class="text-sm font-medium text-gray-600">
            {{ $t("roadmap.sales", seller.sales) }}
          </p>
          <div
            class="mt-1 inline-flex items-center justify-center rounded-full px-2.5 py-1 text-sm font-bold"
            :class="seller.hasBonus ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-gray-100 text-gray-500'"
          >
            <span v-if="seller.hasBonus">+{{ seller.bonus }} €</span>
            <span v-else>0 €</span>
            <span class="ml-1" :class="seller.hasBonus ? 'opacity-100' : 'opacity-60'">
              {{ seller.hasBonus ? "✅" : "❌" }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <hr class="mt-8 mb-2 border-t-2 border-gray-800" />

    <p v-if="loading" class="text-sm text-gray-500 text-center">Chargement des ventes…</p>

    <template v-else>
      <div>
        <div class="mt-4 flex items-center justify-center">
          <span class="inline-flex items-center rounded-full px-2.5 py-1 text-md font-bold bg-gray-900 text-white">
            RÉSULTATS DE LA JOURNÉE
          </span>
        </div>
        <!-- Message objectif ou paliers max -->
        <div v-if="nextTier" class="px-3 py-2 text-center text-lg">
          <p class="font-semibold text-primary-dark">
            <span class="text-lg font-semibold text-gray-900 mb-3">
              {{ $t("roadmap.sales", salesCount ?? 0) }} aujourd'hui
            </span>
            <span class="mx-2 text-black">•</span>
            {{
              $t("saturdayChallenge.moreToUnlock", {
                sales: $t("roadmap.sales", nextTier.salesThreshold - (salesCount ?? 0)),
                bonus: nextTier.bonusPerSeller,
              })
            }}
          </p>
        </div>
        <div v-else-if="reachedTier" class="px-3 py-2 text-center text-lg">
          <p class="font-semibold text-green-800">
            <span class="text-lg font-semibold text-gray-900 mb-3">
              {{ $t("roadmap.sales", salesCount ?? 0) }} aujourd'hui
            </span>
            <span class="mx-2 text-black">•</span>
            🎉 Tous les paliers sont atteints !
          </p>
        </div>

        <!-- Barre de progression (barre + points uniquement), affichée à partir de 1 vente -->
        <div v-if="(salesCount ?? 0) >= 1" class="tiered-progress-bar-bar-only">
          <TieredProgressBar
            :markers="saturdayChallengeTiers"
            :current-value="salesCount ?? 0"
            threshold-key="salesThreshold"
            id-key="tier"
            label-key="tier"
            :compact="true"
          >
            <template #marker-label>
              <span class="invisible">.</span>
            </template>
            <template #current-value>
              <span class="invisible block h-0 overflow-hidden">.</span>
            </template>
          </TieredProgressBar>
        </div>

        <!-- Vendeurs ayant vendu -->
        <template v-if="sellerRows.length > 0">
          <div class="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div
              v-for="row in sellerRows"
              :key="row.id"
              class="rounded-lg border p-3 text-center shadow-xs transition-shadow hover:shadow-md"
              :class="row.bonus > 0 ? 'border-green-200 bg-white' : 'border-gray-200 bg-white'"
            >
              <p class="text-xl font-semibold text-gray-900 truncate" :title="row.name">{{ row.name }}</p>
              <p class="mt-1 text-lg font-medium text-gray-600">
                {{ $t("roadmap.sales", row.contractCount) }}
              </p>
              <div
                v-if="reachedTier"
                class="mt-2 inline-flex items-center justify-center rounded-full px-2.5 py-1 text-lg font-bold"
                :class="
                  row.bonus > 0 ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-gray-100 text-gray-500'
                "
              >
                <span v-if="row.bonus > 0">+{{ row.bonus }} €</span>
                <span v-else>—</span>
                <span v-if="row.bonus > 0" class="ml-1">✅</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <p class="mt-6 text-sm text-gray-500 text-center italic">
      PS : Les ventes d'aujourd'hui via RIB enregistré comptent pour le challenge, même si elles sont confirmées plus
      tard.
    </p>
  </div>
</template>

<script>
import { TieredProgressBar } from "@estaly/ui";
import { mapState, mapActions } from "vuex";
import {
  SATURDAY_CHALLENGE_DATE as CHALLENGE_DATE,
  saturdayChallengeTiers,
  getReachedTier,
  getNextTier,
  getBonusPerSeller,
} from "@/utils/saturdayChallenge";

export default {
  components: {
    TieredProgressBar,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      saturdayChallengeTiers,
      salesCount: null,
      challengeRecords: [],
      loading: false,
      exampleSellers: [
        { name: "Eric", sales: 2, bonus: 20, hasBonus: true },
        { name: "Mathis", sales: 3, bonus: 20, hasBonus: true },
        { name: "Baptiste", sales: 1, bonus: 20, hasBonus: true },
        { name: "Samy", sales: 0, bonus: 0, hasBonus: false },
      ],
    };
  },
  computed: {
    ...mapState("store", ["selectedStoreId"]),
    reachedTier() {
      return this.salesCount !== null ? getReachedTier(this.salesCount) : null;
    },
    nextTier() {
      return this.salesCount !== null ? getNextTier(this.salesCount) : null;
    },
    bonusPerSeller() {
      return this.salesCount !== null ? getBonusPerSeller(this.salesCount) : 0;
    },
    sellerRows() {
      if (!this.challengeRecords?.length) return [];
      const bonus = this.bonusPerSeller;
      const bySeller = new Map();
      for (const record of this.challengeRecords) {
        const seller = record.seller;
        if (!seller?.id) continue;
        const displayName = [seller.firstName, seller.lastName].filter(Boolean).join(" ") || "—";
        if (!bySeller.has(seller.id)) {
          bySeller.set(seller.id, { id: seller.id, name: displayName, contractCount: 0, bonus });
        }
        bySeller.get(seller.id).contractCount += 1;
      }
      return Array.from(bySeller.values()).sort((a, b) => b.contractCount - a.contractCount);
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive && Number(this.selectedStoreId) > 0) {
          this.fetchChallengePlanSales();
        } else if (!isActive) {
          this.salesCount = null;
          this.challengeRecords = [];
        }
      },
    },
    selectedStoreId(newId) {
      if (this.active && Number(newId) > 0) {
        this.fetchChallengePlanSales();
      }
    },
  },
  methods: {
    ...mapActions("planSales", ["fetchIndex"]),
    ...mapActions("leads", { fetchLeadsIndex: "fetchIndex" }),
    isTierReached(tier) {
      return this.salesCount !== null && this.salesCount >= (tier.salesThreshold || 0);
    },
    async fetchChallengePlanSales() {
      if (!this.selectedStoreId) return;
      this.loading = true;
      this.salesCount = null;
      this.challengeRecords = [];
      try {
        const [planSalesData, leadsData] = await Promise.all([
          this.fetchIndex({
            storeIds: [this.selectedStoreId],
            purchasedAtStartDate: CHALLENGE_DATE,
            purchasedAtEndDate: CHALLENGE_DATE,
            page: 1,
            perPage: 1000,
            includes: ["seller"],
          }),
          this.fetchLeadsIndex({
            store: [this.selectedStoreId],
            type: ["lead"],
            status: ["waiting_for_confirmation"],
            startDate: CHALLENGE_DATE,
            endDate: CHALLENGE_DATE,
            page: 1,
            perPage: 1000,
            includes: ["seller"],
          }),
        ]);

        const planTotal = planSalesData?.page?.totalCount ?? 0;
        const planRecords = (planSalesData?.records ?? []).map((r) => ({ seller: r.seller }));

        const leadsTotal = leadsData?.page?.totalCount ?? 0;
        const leadRecords = (leadsData?.leads ?? []).map((lead) => ({ seller: lead.seller }));

        this.salesCount = planTotal + leadsTotal;
        this.challengeRecords = [...planRecords, ...leadRecords];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Afficher uniquement la barre et les points (masquer labels au-dessus et pill en dessous) */
.tiered-progress-bar-bar-only :deep(> div) {
  padding: 0.5rem 0;
  background: transparent;
}
.tiered-progress-bar-bar-only :deep(> div > div > div:first-child) {
  display: none;
}
.tiered-progress-bar-bar-only :deep(> div > div > div:last-child) {
  display: none;
}
</style>
